// src/portal/PortalLauncherPage.jsx
//
// THE PORTAL'S INDEX. Ported from docs/design/opportal-mock/opportal-landing.html.
//
// A launcher: a title, a blurb, a read stamp with Refresh, and a grid of cards
// -- one per portal surface exposing a rollup -- with a dashed slot for the
// next. What shipped before this collapsed the launcher into the list screen:
// the rollup card sat directly above the rows with no index above it.
//
// ── IT IS THE PORTAL'S INDEX, NOT A PLATFORM PRODUCT DASHBOARD ─────────────
//
// The mock's blurb reads "Operational analytics across AI products", which is
// wider than what this is. WellFinder and PadRekko are not cards here and are
// not going to be -- they have their own surfaces reached from the product
// strip. What joins this grid is portal surfaces: plan reviews today, and
// operator-side things after it. The blurb says that.
//
// ── A CONFINED SUBMITTER NEVER GETS HERE ───────────────────────────────────
//
// They have exactly one surface, so a one-card launcher in front of it is a
// click in the way. Their home IS the list, and HOMES[PORTAL_HOME].reaches
// admits '/portal/...' but not '/portal' itself, so the redirect that already
// exists sends them past this page. Nothing in this file knows about it, which
// is the point -- see router.jsx and PortalShellSelect.jsx.
//
// ── THE EMPTY SLOT SHIPS ───────────────────────────────────────────────────
//
// It is a deliverable, not decoration: honest that there is one surface today
// and it shows the shape the portal grows into. The mock's dashed treatment and
// copy, with one word changed -- "dashboard" to "surface" -- for the same
// reason the blurb changed.
import { useCallback, useEffect, useState } from "react";
import { RefreshCw, LayoutGrid, Plus } from "lucide-react";
import { listPlanReviews, PortalError } from "../api/portalApi";
import { rollupFrom } from "./portalRollup";
import PortalRollupCard, { QUEUE_PREVIEW } from "./PortalRollupCard";

/** HH:MM in the reader's own zone, with the zone named. The mock's "READ 09:42
 *  CDT" -- a stamp is only useful if you can tell whether it is stale. */
function readStamp(at) {
  if (!at) return null;
  const time = at.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
  const zone = new Intl.DateTimeFormat([], { timeZoneName: "short" })
    .formatToParts(at).find((p) => p.type === "timeZoneName")?.value ?? "";
  return `read ${time} ${zone}`.trim();
}

export default function PortalLauncherPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [readAt, setReadAt] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      setData(await listPlanReviews());
      setError(null);
      setReadAt(new Date());
    } catch (e) {
      setError(e instanceof PortalError ? e : new PortalError(0, { error: String(e) }));
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (error) {
    return (
      <div className="op-portal-surface flex min-h-0 flex-1 flex-col overflow-auto p-6 text-sm text-fg-secondary">
        <p className="text-red-400">{error.message}</p>
        {error.code && <p className="mt-1 font-mono text-xs opacity-70">{error.code}</p>}
      </div>
    );
  }

  if (!data) {
    return (
      <div className="op-portal-surface flex min-h-0 flex-1 flex-col overflow-auto p-6 text-sm text-fg-secondary">
        Loading…
      </div>
    );
  }

  // ── THE SAME DERIVATIONS THE LIST USES, AND NOT A SECOND SET ─────────────
  //
  // `scope` and `awaiting_statuses` are the server's, from route 5. The client
  // never re-derives either: awaiting_statuses is the set of statuses this
  // caller may WRITE at, computed from mayWritePlanReview, so the card's
  // "awaiting you" and the list's rows cannot disagree and both moved when
  // PLAN_REVIEW_WRITER moved.
  //
  // THE TRUNCATION IS THE DISPLAY'S, and it is the only thing decided here:
  // slice to QUEUE_PREVIEW for the rows, and report the rest as a count off the
  // FULL length. A second filter would be a second predicate.
  const isSubmitter = data.scope === "submitter";
  const awaitingStatuses = data.awaiting_statuses ?? [];
  const awaiting = (data.reviews ?? []).filter((r) => awaitingStatuses.includes(r.status));
  const rollup = rollupFrom(data.counts);

  return (
    <div className="op-portal-surface flex min-h-0 flex-1 flex-col gap-6 overflow-auto p-6">

      <div className="flex items-start justify-between gap-8">
        <div className="flex min-w-0 max-w-[700px] flex-col gap-2">
          <div className="flex items-center gap-2.5">
            <LayoutGrid className="h-5 w-5 shrink-0 text-accent-action" aria-hidden="true" />
            <h1 className="font-display text-[22px] font-semibold tracking-[-0.01em] text-fg-primary">
              SurfacePlanner
            </h1>
          </div>
          <p className="text-[13px] leading-relaxed text-fg-secondary">
            The portal's surfaces and their rollups. Figures on each card are read at page
            load; opening a card fetches its full detail fresh.
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-3">
          {readAt && (
            <span
              data-testid="read-stamp"
              className="font-mono text-[10px] uppercase tracking-[0.09em] text-fg-secondary"
            >
              {readStamp(readAt)}
            </span>
          )}
          <button
            type="button"
            onClick={load}
            disabled={refreshing}
            className="op-btn inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} aria-hidden="true" />
            Refresh
          </button>
        </div>
      </div>

      {/* The mock's three-column grid: the card claims two so the queue preview
          fits beside the rollup, and the slot takes the third. */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <PortalRollupCard
            rollup={rollup}
            awaiting={awaiting.slice(0, QUEUE_PREVIEW)}
            totalAwaiting={awaiting.length}
            openTo="/portal/plan-reviews"
            title="Plan Reviews"
            // ── THE OPERATOR'S CARD IS THE SAME CARD, DIFFERENT NUMBERS ────
            //
            // The mock draws the owner's side only. An operator's rollup counts
            // THEIR OWN submissions and their awaiting-you is changes_requested
            // plus draft, so "Plans submitted to this organization for review"
            // is not merely unhelpful for them, it is false.
            blurb={isSubmitter
              ? "Pad and access-road plans you have submitted, marked up and returned until approved."
              : "Operator pad and access-road submittals, marked up and returned until approved."}
            emptyLine={isSubmitter
              ? "Nothing is waiting on you."
              : "Nothing is waiting on you — no plan is with the reviewer."}
            stamp={`${data.counts.total} total`}
          />
        </div>

        {/* ── The empty slot. Ships. ──────────────────────────────────────── */}
        <div
          data-testid="next-surface-slot"
          className="flex min-h-[220px] flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-bg-border p-6 text-center"
        >
          <Plus className="h-6 w-6 text-fg-secondary opacity-50" aria-hidden="true" />
          <span className="text-[13px] text-fg-secondary">Next surface slot</span>
          <span className="max-w-[230px] text-xs leading-relaxed text-fg-secondary opacity-70">
            Cards appear here as each portal surface exposes its rollup.
          </span>
        </div>
      </div>
    </div>
  );
}
